import { ChangeEvent, FC, ForwardedRef, forwardRef, useEffect, useState, useRef, useLayoutEffect } from 'react';

import { useTheme } from '@emotion/react';
import CharacterCount from '../Input/CharacterCount/CharacterCount';
import { Container, LabelWrapper, StyledLabel, ErrorText, HelpText } from '../Input/Input.styles';
import { TextareaProps } from './Textarea.types';
import TextareaDragHandle from '../Icons/TextareaDragHandle/TextareaDragHandle';
import { IconWrapper, StyledTextarea, TextAreaWrapper } from './Textarea.styles';

const Textarea: FC<TextareaProps> = forwardRef(
  (
    { label, onChange, helpText, errorText, characterLimit, value, id, rows = 4, resizable = false, ...props },
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [textareaWidth, setTextareaWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    useEffect(() => {
      setInputValue(value || '');
    }, [value]);

    // sets textarea width to match container width when window is resized
    // this is necessary because TextareaWrapper needs width "min-content" to position the drag icon
    // this is also untestable because jsdom does not give elements widths: https://github.com/jsdom/jsdom#unimplemented-parts-of-the-web-platform
    /* istanbul ignore next */
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setTextareaWidth(width);
      }
    };
    useEffect(() => {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    // sets textarea width to match container width before the browser has a chance to paint on initial render
    useLayoutEffect(() => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setTextareaWidth(width);
      }
    }, [containerRef.current]);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(event);
      }
      setInputValue(event.target.value);
    };

    return (
      <Container ref={containerRef}>
        <LabelWrapper>
          <StyledLabel htmlFor={id}>{label}</StyledLabel>
          {helpText !== undefined ? <HelpText>{helpText}</HelpText> : <></>}
        </LabelWrapper>
        <TextAreaWrapper>
          <StyledTextarea
            {...props}
            id={id}
            as={'textarea'}
            ref={ref}
            onChange={handleChange}
            value={inputValue}
            errorText={errorText}
            rows={rows}
            resizable={resizable}
            width={textareaWidth}
          />
          <IconWrapper resizable={resizable}>
            <TextareaDragHandle fontSize='inherit' htmlColor={errorText ? theme.colors.red : theme.colors.gray400} />
          </IconWrapper>
        </TextAreaWrapper>
        {characterLimit ? <CharacterCount charactersRemaining={characterLimit - (inputValue?.length || 0)} /> : <></>}
        {errorText ? (
          <ErrorText characterLimit={characterLimit} aria-live='assertive'>
            {errorText}
          </ErrorText>
        ) : (
          <></>
        )}
      </Container>
    );
  }
);

export default Textarea;
